import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { cn } from "../../../utils/cn";
import { Button } from "../../button";
import { Process, Role } from "../../../constants/common";
import { ColumnDef } from "@tanstack/react-table";
import TableWrapper from "../TableWrapper";
import { useTranslation } from "react-i18next";
import { GetAllRolesResponse } from "../../../hooks/common/interface";
import { ArrowUpDown } from "lucide-react";

interface ProcessTableProps {
  roles?: GetAllRolesResponse[];
  processes: Process[];
  selectedProcessId: number;
  setSelectedProcessId: Dispatch<SetStateAction<number>>;
  setChoosenRoles: Dispatch<
    SetStateAction<
      {
        id: number;
        label: string;
      }[]
    >
  >;
  setFilteredRoles: Dispatch<
    SetStateAction<
      {
        id: number;
        label: string;
      }[]
    >
  >;
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  count: number;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const ProcessTable = ({
  processes,
  selectedProcessId,
  setSelectedProcessId,
  roles,
  setChoosenRoles,
  setFilteredRoles,
  page,
  pages,
  setPage,
  count,
  pageSize,
  setPageSize,
}: ProcessTableProps) => {
  const { t, i18n } = useTranslation();

  const [selectedKey, setSelectedKey] = useState("");

  const handleProcessSelection = useCallback(
    (id: number, key: string) => {
      setChoosenRoles([
        {
          id: 0,
          label: t("selectAll"),
        },
      ]);

      setSelectedProcessId(id);
      setSelectedKey(key);

      if (!roles) return;

      const computedRoles = [
        { id: 0, label: t("selectAll") },
        ...roles.map((item: Role) => ({
          id: item.n_RoleID,
          label: i18n.language === "en" ? item.s_RoleName : item.s_RoleNameAr,
        })),
      ];

      setFilteredRoles(computedRoles);
    },
    [
      i18n.language,
      roles,
      setChoosenRoles,
      setFilteredRoles,
      setSelectedProcessId,
      t,
    ]
  );

  const columns: ColumnDef<Process>[] = useMemo(
    () => [
      {
        accessorKey: "s_ProcessName",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("processName")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_ProcessName
                : row.original.s_ProcessName_Ar}
            </span>
          );
        },
      },
      {
        header: t("action"),
        cell: ({ row }) => {
          return (
            <Button
              onClick={() =>
                handleProcessSelection(row.original.n_ProcessID, "n_ProcessID")
              }
              type="button"
              className={cn("py-0.5 px-3 min-w-[80px]")}
            >
              {selectedProcessId === row.original.n_ProcessID
                ? t("selected")
                : t("select")}
            </Button>
          );
        },
      },
    ],
    [handleProcessSelection, i18n.language, selectedProcessId, t]
  );

  return (
    <TableWrapper
      data={processes}
      columns={columns}
      isColumnChooser={false}
      page={page}
      setPage={setPage}
      totalPages={pages}
      count={count}
      selectedKey={selectedKey}
      selectedRowId={selectedProcessId}
      isDarkModeStyles={false}
      pageSize={pageSize}
      setPageSize={setPageSize}
    />
  );
};

export default React.memo(ProcessTable);
